/* eslint-disable no-mixed-operators */
import React, { useState, useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { Button, Box, Popover } from '@mui/material'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { buttonVariants } from './button'
import { cn } from '../../lib/utils'
import { CalendarIcon } from '@radix-ui/react-icons'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import dayjs from 'dayjs'

const DatePickerInput = ({ mode = 'single', resetTrigger, className, disabled, onValueChange, readOnly, value, name = '', errors, rules, control, label, required, lableComponent, minDate, maxDate }) => {
    const [anchorEl, setAnchorEl] = React.useState(null)
    const [selectedDate, setSelectedDate] = useState(value || null)
    const [month, setMonth] = useState(dayjs(selectedDate).month())
    const [year, setYear] = useState(dayjs(selectedDate).year())

    const handleClick = (event) => {
        setAnchorEl(event?.currentTarget)
    }
    const months = dayjs.months();
    const currentYear = dayjs().year()
    const earliestYear = 1950
    const years = Array.from({ length: (currentYear + 2) - earliestYear + 1 }, (_, i) => earliestYear + i)
    const handleClose = () => {
        setAnchorEl(null)
    }
    const handleMonthChange = (newMonth) => {
        const monthIndex = months.indexOf(newMonth);
        setMonth(monthIndex);
        setSelectedDate(new Date(year, monthIndex, dayjs(selectedDate).date()));
    }
    const handleYearChange = (newYear) => {
        setYear(newYear);
        setSelectedDate(new Date(newYear, month, dayjs(selectedDate).date()));
    }
    const handleDaySelect = (date) => {
        setSelectedDate(date);
        onValueChange(date, name);
        handleClose();
    }
    const valueKey = value && !isNaN(new Date(value).getTime())
        ? dayjs(value).format('YYYY-MM-DD')
        : null
    useEffect(() => {
        const parsed = value ? new Date(value) : null;
        const isValid = parsed && !isNaN(parsed.getTime());
        setSelectedDate(isValid ? parsed : null);
        setMonth(isValid ? parsed.getMonth() : new Date().getMonth());
        setYear(isValid ? parsed.getFullYear() : new Date().getFullYear());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [valueKey])

    return (
        <Box >
            {label && (
                <label className={cn('flex w-full flex-row')}>
                    <span className={cn('grc-label whitespace-nowrap block flex flex-row')}>{label}</span>
                    {required && <span className="text-danger">*</span>}
                    <span className="">{lableComponent}</span>
                </label>
            )}
            <Controller
                name={name}
                control={control}
                rules={rules}
                defaultValue={value || ''}
                render={({ field }) => (
                    <Box sx={{ mt: '4px' }}>
                        <Popover
                            open={anchorEl}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: 'center'
                            }}
                            transformOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center'
                            }}
                            sx={{ zIndex: 10000 }}
                        >
                            <Box padding={2}>
                                <Box display="flex" columnGap={2} justifyContent="space-between" mb={2}>
                                    <Select onValueChange={handleMonthChange} value={months[month]} disabled={disabled}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Month" />
                                        </SelectTrigger>
                                        <SelectContent style={{ zIndex: 10001 }}>
                                            {months.map((monthName, index) => (
                                                <SelectItem key={index} value={monthName}>
                                                    {monthName}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select onValueChange={handleYearChange} value={year}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Year" />
                                        </SelectTrigger>
                                        <SelectContent style={{ zIndex: 10001 }}>
                                            {years.map((yearValue) => (
                                                <SelectItem key={yearValue} value={yearValue}>
                                                    {yearValue}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Box>
                                <DayPicker
                                    mode={mode}
                                    selected={selectedDate}
                                    showOutsideDays={true}
                                    onSelect={(date) => {
                                        if (date) {
                                            setSelectedDate(date)
                                            onValueChange(date, name)
                                            field.onChange(date)
                                            handleClose()
                                        }
                                    }}
                                    month={new Date(year, month)}
                                    disabled={date => (minDate && date < new Date(minDate).setHours(0, 0, 0, 0)) || (maxDate && date > new Date(maxDate).setHours(23, 59, 59, 999))}
                                    classNames={{
                                        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
                                        month: 'space-y-3',
                                        caption_start: 'is-start',
                                        caption_between: 'is-between',
                                        caption_end: 'is-end',
                                        caption: 'flex justify-center pt-1 items-center text-center',
                                        caption_label: 'flex h-7 text-sm font-medium justify-center items-center grow [.is-multiple_&]:flex',
                                        caption_dropdowns: 'flex justify-center grow dropdowns pl-7 pr-8',
                                        multiple_months: 'is-multiple',
                                        vhidden: 'hidden [.is-between_&]:flex [.is-end_&]:flex [.is-start.is-end_&]:hidden',
                                        nav: "flex items-center [&:has([name='previous-month'])]:order-first [&:has([name='next-month'])]:order-last",
                                        nav_button: cn(
                                            buttonVariants({ variant: 'outline' }),
                                            'h-6 w-6 bg-transparent p-0 text-primary'
                                        ),
                                        nav_button_previous: 'hidden',
                                        nav_button_next: 'hidden',
                                        table: 'w-full border-collapse space-y-1',
                                        head_row: 'flex',
                                        head_cell: 'text-primary rounded-md w-8 font-normal text-[0.8rem]',
                                        row: 'flex w-full mt-2',
                                        cell: cn(
                                            'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent',
                                            '[&:has([aria-selected])]:rounded-md'
                                        ),
                                        day: cn(
                                            buttonVariants({ variant: 'ghost' }),
                                            'h-8 w-8 p-0 font-normal aria-selected:opacity-100'
                                        ),
                                        day_range_start: 'day-range-start',
                                        day_range_end: 'day-range-end',
                                        day_selected:
                                            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
                                        day_today: 'bg-accent text-accent-foreground',
                                        day_outside: 'text-muted-foreground opacity-50',
                                        day_disabled: 'text-muted-foreground opacity-50',
                                        day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
                                        day_hidden: 'invisible'
                                    }}
                                    components={{
                                        Dropdown: ({ ...props }) => (
                                            <Select
                                                {...props}
                                                onValueChange={(e) => { onValueChange(e, name) }}
                                                value={props.value}
                                            >
                                                <SelectTrigger className={cn(buttonVariants({ variant: 'ghost' }), 'pl-2 pr-1 py-2 h-7 w-fit font-medium [.is-between_&]:hidden [.is-end_&]:hidden [.is-start.is-end_&]:flex')}>
                                                    <SelectValue placeholder={props?.caption} >{props?.caption}</SelectValue>
                                                </SelectTrigger>
                                                <SelectContent className="max-h-[var(--radix-popper-available-height);] overflow-y-auto scrolling-auto min-w-[var(--radix-popper-anchor-width)]">
                                                    {props.children &&
                                                        React.Children.map(props.children, (child) =>
                                                            <SelectItem value={(child)?.props?.value} className="min-w-[var(--radix-popper-anchor-width)]">{(child)?.props?.children}</SelectItem>
                                                        )
                                                    }
                                                </SelectContent>
                                            </Select>
                                        )
                                    }}
                                />
                            </Box>
                        </Popover>
                        <Button
                            variant={'dateInput'}
                            disabled={disabled}
                            onClick={handleClick}
                            sx={{ border: '2px solid hsl(var(--border))', backgroundColor: disabled && 'hsl(var(--popover))', borderRadius: '8px' }}
                            className={cn(
                                'w-full justify-start mt-[5px] text-left font-normal',
                                !value || new Date() && 'text-muted-foreground',
                                'date-pick-btn',
                                'whitespace-nowrap',
                                className
                            )}
                            style={{ color: dayjs(value || new Date()).format('DD-MM-YYYY') ? 'hsl(var(--muted))' : 'hsl(var(--border))' }}
                        >
                            {value ? dayjs(value || new Date()).format('DD-MM-YYYY') : <span style={{ color: 'hsl(var(--border))' }}>dd-mm-yyyy</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 " style={{ color: disabled ? 'hsl(var(--border))' : 'hsl(var(--primary))' }} />
                        </Button>
                    </Box>
                )}
            />
            {errors && name && errors[name] && <p className='grc-error-msg text-danger'>{errors && name && errors[name]?.message}</p>}
        </Box>
    )
}

export default DatePickerInput